import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { setAuthToken } from "@/lib/api";
import { isFirebaseConfigured, signInWithGoogle, signOutFirebase } from "@/lib/firebase";

const STORAGE_KEY = "org3.session";
const AUTHORIZED_EMAIL = (process.env.REACT_APP_AUTHORIZED_EMAIL || "").toLowerCase();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [twoFactorVerifiedAt, setTwoFactorVerifiedAt] = useState(null);

  // Load session from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s?.email) {
          setAuthToken(s.email); // scaffold: token IS the email
          setSession(s);
        }
      }
    } catch (_) {
      // ignore malformed session
    }
    setReady(true);
  }, []);

  const persist = useCallback((s) => {
    if (s) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
      setAuthToken(s.email);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setAuthToken(null);
    }
    setSession(s);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (!isFirebaseConfigured) {
      throw new Error(
        "Firebase non configurato. Usa 'Accedi in modalità scaffold' oppure aggiungi le variabili REACT_APP_FIREBASE_* al file frontend/.env",
      );
    }
    const { email } = await signInWithGoogle();
    const normalized = (email || "").toLowerCase();
    if (AUTHORIZED_EMAIL && normalized !== AUTHORIZED_EMAIL) {
      await signOutFirebase();
      throw new Error(`Email non autorizzata: ${email}`);
    }
    persist({ email: normalized, provider: "google", loggedAt: new Date().toISOString() });
  }, [persist]);

  const loginMock = useCallback(() => {
    const email = AUTHORIZED_EMAIL || "owner@org3.ai";
    persist({ email, provider: "mock", loggedAt: new Date().toISOString() });
  }, [persist]);

  const logout = useCallback(async () => {
    try {
      await signOutFirebase();
    } catch (_) {
      // ignore signout errors — we clear local session anyway
    }
    setTwoFactorVerifiedAt(null);
    persist(null);
  }, [persist]);

  const markTwoFactorVerified = useCallback(() => {
    setTwoFactorVerifiedAt(Date.now());
  }, []);

  // Verification window: 5 minutes
  const isTwoFactorFresh = useMemo(() => {
    if (!twoFactorVerifiedAt) return false;
    return Date.now() - twoFactorVerifiedAt < 5 * 60 * 1000;
  }, [twoFactorVerifiedAt]);

  const value = useMemo(
    () => ({
      session,
      ready,
      isAuthenticated: Boolean(session?.email),
      isFirebaseConfigured,
      authorizedEmail: AUTHORIZED_EMAIL,
      loginWithGoogle,
      loginMock,
      logout,
      isTwoFactorFresh,
      markTwoFactorVerified,
    }),
    [session, ready, loginWithGoogle, loginMock, logout, isTwoFactorFresh, markTwoFactorVerified],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
